import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components/macro';
import { useDispatch } from 'react-redux';
import { useCookies } from 'react-cookie';
import { useHistory, useLocation, useParams, useRouteMatch } from 'react-router';
import { Link } from 'react-router-dom';
import { capFirst } from '@tovia/man-app-utils/lib/helpers/converters';
import { Pagination, SortDropdown } from '@tovia/man-ui';
import PaginationModal from 'src/client/components/modals/PaginationModal';
import LoaderWrapper from 'src/client/components/Placeholders/Layouts/SearchResultsPlacholder';
import { load as loadSearchResultsFn } from 'src/client/redux/modules/searchResults';
import { SORT_TYPE_RELEVANCE, SORT_TYPE_LATEST_GALLERY, SORT_TYPES } from 'src/shared/constants/search';
import ConnectedViewOptions from '../../ConnectedViewOptions';
import { GalleryCards } from '../../Cards/Gallery/GalleryCards';
import ModelCards from '../../Cards/Model/ModelCards';
import { useSelector, useSettingsSelector } from 'src/client/redux/modules/helpers/useSelector';
import { isPrerender } from 'src/client/utils/prerenderDetect';
import { UserTypes } from 'src/shared/constants/userTypes';
import { MetArt } from 'src/client/constants/sites';
import { MOBILE_LANDSCAPE } from 'src/client/constants/mediaQueries';
import Helmet from '../../Helmet/Helmet';
import { OnPageText } from 'src/client/components/OnPageSeo/OnPageText/OnPageText';
import { InjectedSeoText } from 'src/client/components/OnPageSeo/InjectedSeoText';
import { SearchResultGallery, SearchResultModel } from '../../../../@types/SearchResultsTypes';
import { useIsMobile } from 'src/client/utils/useIsMobile';
import { SEARCH_SORT_COOKIE } from 'src/shared/constants/cookies';
import { Models } from 'src/client/redux/modules/models';

export default function SearchResults() {
  const { items, suggestions, itemCount, loaded, error, pageSize, fullTotal, redirectURL, displayName } = useSelector(
    (state) => state.searchResults,
  );
  const site = useSelector((state) => state.site);

  const dispatch = useDispatch();
  const loadSearchResults = useCallback((args) => dispatch(loadSearchResultsFn(args)), [dispatch]);

  const { searchTerm, page, sortBy: paramsSortBy } = useParams<{ searchTerm: string; page: string; sortBy: string }>();
  const location = useLocation();
  const history = useHistory();
  const match = useRouteMatch();

  const [cookies, setCookie] = useCookies([SEARCH_SORT_COOKIE]);
  const cookieSortBy = cookies[SEARCH_SORT_COOKIE];

  const cardViewType = useSettingsSelector('listView');

  const { loaded: userLoaded, userType } = useSelector((state) => state.auth);
  const defaultSortByUserType = userType === UserTypes.Guest ? SORT_TYPE_LATEST_GALLERY.id : SORT_TYPE_RELEVANCE.id;

  const route = match.url.split('/')[1];
  const isTagPage = route === 'tags';
  const [sortBy, setSortBy] = useState<undefined | string>();
  const isMobile = useIsMobile();
  const displayResultsInfo = fullTotal > itemCount;

  useEffect(() => {
    if (userLoaded) {
      setSortBy(paramsSortBy || cookieSortBy || defaultSortByUserType);
    }
  }, [paramsSortBy, cookieSortBy, defaultSortByUserType, userLoaded]);

  const loadData = useCallback(() => {
    const { search } = location;

    const queryString = new URLSearchParams(search);
    const validKeys = [
      'pubicHair',
      'hair',
      'eyes',
      'ethnicity',
      'country',
      'breasts',
      'age',
      'photographers',
      'models',
      'modelCount',
      'contentType',
      'weightSlider',
      'heightSlider',
    ];

    const query = Array.from(queryString).reduce((acc, [key, value]) => {
      if (validKeys.includes(key)) {
        acc[key] = value;
      }
      return acc;
    }, {});

    const options = {
      ...(searchTerm && searchTerm !== '-'
        ? {
            searchPhrase: searchTerm,
          }
        : {}),
      page: page || 1,
      pageSize,
      sortBy,
      query,
    };

    loadSearchResults(options);
  }, [loadSearchResults, location, page, pageSize, searchTerm, sortBy]);

  useEffect(() => {
    if (userLoaded && sortBy) {
      loadData();
    }
  }, [loadData, searchTerm, sortBy, route, page, userLoaded]);

  const getSortUrl = useCallback((value) => `/${route}/${searchTerm}/${value}${location.search || ''}`, [
    location.search,
    searchTerm,
    route,
  ]);

  const changeSort = useCallback(
    (value) => {
      setSortBy(value);

      setCookie(SEARCH_SORT_COOKIE, value, {
        domain: site.domain,
        path: '/',
      });

      history.push(getSortUrl(value));
    },
    [getSortUrl, site, setCookie, setSortBy, history],
  );

  const pageUrlPrefix = useMemo(() => `/${route}/${searchTerm}/${sortBy}`, [route, searchTerm, sortBy]);
  const pageUrlSuffix = useMemo(() => `/${location.search || ''}`, [location.search]);
  const setPage = useCallback(
    (newPage) => {
      history.push(`${pageUrlPrefix}/${newPage}${pageUrlSuffix}`);
    },
    [pageUrlPrefix, history, pageUrlSuffix],
  );

  const renderResultsCount = useCallback(
    (term, count) => {
      const hideCount = site?.abbreviation === MetArt.abbreviation && userType === UserTypes.Guest;
      return (
        <ResultsCount>
          {term}
          {!hideCount && ` - ${new Intl.NumberFormat().format(count)} items found `}
        </ResultsCount>
      );
    },
    [site, userType],
  );

  const renderResults = (term) => {
    const galleries: SearchResultGallery[] = [];
    const models: SearchResultModel[] = [];

    items.forEach(({ item, type }) => {
      switch (type) {
        case 'GALLERY':
        case 'MOVIE': {
          return galleries.push(item);
        }
        case 'model':
          return models.push(item);
        default:
          return null;
      }
    });

    return itemCount > 0 ? (
      <div>
        {isMobile ? renderResultsCount(term, fullTotal) : null}
        <OptionsContainer displayResultsInfo={displayResultsInfo}>
          <div>
            {!isMobile ? renderResultsCount(term, fullTotal) : null}
            <ResultsInfo>
              {displayResultsInfo ? `Showing top ${new Intl.NumberFormat().format(itemCount)} results` : null}
            </ResultsInfo>
          </div>
          {isPrerender && (
            <div>
              Sort By:{' '}
              <div>
                {SORT_TYPES.map((sortType) => {
                  const link = getSortUrl(sortType.id);
                  return (
                    <Link key={sortType.id} to={link}>
                      {' '}
                      {sortType.title}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
          <OptionsWrapper>
            <Options>
              {sortBy && (
                <SortDropdownWrapper>
                  <SortDropdown menuOptions={SORT_TYPES} value={sortBy} onChange={changeSort} />
                </SortDropdownWrapper>
              )}
              <ConnectedViewOptions />
            </Options>
          </OptionsWrapper>
        </OptionsContainer>
        <OnPageText contentName="text1" />
        <>
          <GalleryCards
            tag={isTagPage ? term : undefined}
            galleries={galleries}
            seoInjectedComponent={
              <InjectedSeoText key="injected-seo-component" index={3}>
                <OnPageText contentName="text2" />
              </InjectedSeoText>
            }
          />
          <ModelCards models={models as Models[]} />
        </>
        <Pagination
          activePage={parseInt(page) || 1}
          boundaryRange={1}
          onPageChange={setPage}
          siblingRange={1}
          totalPages={Math.ceil(itemCount / pageSize)}
          showPrevNext
          pageUrlPrefix={pageUrlPrefix}
          pageUrlSuffix={pageUrlSuffix}
          modal={
            <PaginationModal
              setPage={(pageNumber) => setPage(pageNumber)}
              totalPages={Math.ceil(itemCount / pageSize)}
            />
          }
        />
      </div>
    ) : (
      <div>
        <NoMatchTitle>
          <strong>
            No items match <em>{term}</em>.
          </strong>
        </NoMatchTitle>
        {suggestions && suggestions.length ? (
          <p>
            Did you mean:{' '}
            {suggestions
              .map((v, i) => ({
                ...v,
                key: `${v.text}-${i}`,
              }))
              .map((sugg, i) => (
                <Fragment key={sugg.key}>
                  {i ? ', ' : null}
                  <Link to={`/${route}/${encodeURIComponent(sugg.text)}`}>{sugg.text}</Link>
                </Fragment>
              ))}
            ?
          </p>
        ) : null}
        {renderResultsCount(term, itemCount)}
      </div>
    );
  };

  const capitalizedRoute = capFirst(route);

  const errorMarkup = error ? null : null; // Todo Create Content Error Container
  const searchResultsContentMarkup =
    loaded && userLoaded && sortBy ? (
      renderResults(displayName)
    ) : (
      <LoaderWrapper detailed={cardViewType === 'detailed'} />
    );

  useEffect(() => {
    if (loaded && redirectURL && isTagPage) {
      history.push(redirectURL);
    }
  }, [loaded, redirectURL, history, isTagPage]);

  const helmetParams = {
    id: isTagPage ? [`tag:${searchTerm}`, 'tag'] : [],
    canonicalUrl: `${window.location.origin}/${route}/${searchTerm}`,
    defaultTitle: '{{#displayName}}"{{displayName}}" {{/displayName}}{{capitalizedRoute}} Results @ {{siteName}}',
    variables: {
      displayName,
      lowercasedName: (displayName || searchTerm).replace(/[_-]/g, ' ').toLowerCase(),
      capitalizedRoute,
      siteName: site.name,
    },
  };

  if (items) {
    return (
      <div className="content container">
        <Helmet {...helmetParams}>
          {(route === 'search' || (loaded && itemCount === 0)) && <meta name="robots" content="noindex" />}
          <meta property="prerender:ttl" content="100800" />
        </Helmet>
        {searchResultsContentMarkup}
        {errorMarkup}
      </div>
    );
  }
  return (
    <div className="content container">
      <Helmet {...helmetParams} />
      <LoaderWrapper detailed={cardViewType === 'detailed'} />
    </div>
  );
}

const OptionsContainer = styled.div<{ displayResultsInfo: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: ${({ displayResultsInfo }) => (displayResultsInfo ? 'center' : 'flex-end')};
  flex-wrap: wrap;
`;

const ResultsCount = styled.h1`
  font-size: 1.5rem;

  @media ${MOBILE_LANDSCAPE} {
    font-size: 36px;
  }
`;

const ResultsInfo = styled.span`
  width: 100%;
  display: none;
  font-size: 14px;
  margin-top: 10px;
  margin-bottom: 10px;
  @media ${MOBILE_LANDSCAPE} {
    width: auto;
    display: flex;
  }
`;

const OptionsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  width: auto;
  width: 100%;

  @media ${MOBILE_LANDSCAPE} {
    width: auto;
  }
`;

const Options = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin: 10px 0;
`;

const SortDropdownWrapper = styled.div`
  margin-right: 20px;
`;

const NoMatchTitle = styled.p`
  margin-top: 15px;
`;
